import { useCallback, useState, useEffect, useRef } from "react";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuIcon,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { SearchFilterMenuItemType } from "../FilterMenu.model";

interface SearchFilterMenuItemProps<Field extends string>
  extends SearchFilterMenuItemType<Field> {
  onChange: (item: SearchFilterMenuItemType<Field>) => void;
}

export function SearchFilterMenuItem<Field extends string>({
  onChange,
  ...item
}: SearchFilterMenuItemProps<Field>) {
  const initialValue = item.value?.value.replaceAll("%", "") || item.defaultValue || "";
  const [search, setSearch] = useState(initialValue);
  const hasChangedRef = useRef(false);
  const initialValueRef = useRef(initialValue);

  useEffect(() => {
    const currentValue = item.value?.value.replaceAll("%", "") || item.defaultValue || "";
    setSearch(currentValue);
    initialValueRef.current = currentValue;
    hasChangedRef.current = false;
  }, [item.value, item.defaultValue]);

  const handleSubmit = useCallback((forceSubmit = false) => {
    if (search === "") {
      return;
    }

    if (!forceSubmit && !hasChangedRef.current && search === initialValueRef.current) {
      return;
    }

    onChange({
      ...item,
      value: { operator: "like", value: `%${search}%` },
    });
    hasChangedRef.current = false;
  }, [search, onChange, item]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    hasChangedRef.current = true;
  }, []);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {item.icon && <DropdownMenuIcon>{item.icon}</DropdownMenuIcon>}
        {item.label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent
        sideOffset={8}
        alignOffset={-5}
        className="w-[240px]"
      >
        <Input
          placeholder={item.label}
          value={search}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSubmit(true);
            }
          }}
          onBlur={() => handleSubmit(false)}
          className="w-[214px] h-6"
        />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
